import React, { useState, useEffect } from 'react';
import { Search, SlidersHorizontal, MapPin, Building2, IndianRupee, Calendar, Home, ArrowLeft, X, ChevronDown, CheckCircle } from 'lucide-react';

const AuctionDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState({});
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);
  
  // Available filter options
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('/data/baanknet_full_details.csv');
        const text = await response.text();
        
        // Proper CSV parsing using PapaParse-like logic
        const Papa = {
          parse: (input, config = {}) => {
            const rows = [];
            let currentRow = [];
            let currentCell = '';
            let insideQuotes = false;
            
            for (let i = 0; i < input.length; i++) {
              const char = input[i];
              const nextChar = input[i + 1];
              
              if (char === '"') {
                if (insideQuotes && nextChar === '"') {
                  currentCell += '"';
                  i++; // Skip next quote
                } else {
                  insideQuotes = !insideQuotes;
                }
              } else if (char === ',' && !insideQuotes) {
                currentRow.push(currentCell);
                currentCell = '';
              } else if ((char === '\n' || char === '\r') && !insideQuotes) {
                if (char === '\r' && nextChar === '\n') {
                  i++; // Skip \n in \r\n
                }
                if (currentCell || currentRow.length > 0) {
                  currentRow.push(currentCell);
                  if (currentRow.some(cell => cell.trim())) {
                    rows.push(currentRow);
                  }
                  currentRow = [];
                  currentCell = '';
                }
              } else {
                currentCell += char;
              }
            }
            
            // Add last cell/row
            if (currentCell || currentRow.length > 0) {
              currentRow.push(currentCell);
              if (currentRow.some(cell => cell.trim())) {
                rows.push(currentRow);
              }
            }
            
            if (rows.length === 0) return { data: [] };
            
            const headers = rows[0];
            const data = rows.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header.trim()] = (row[index] || '').trim();
              });
              return obj;
            });
            
            return { data };
          }
        };
        
        const parsed = Papa.parse(text);
        const parsedData = parsed.data
          .filter(row => row.property_id) // Filter out incomplete rows
          .filter(row => row.inspection_start_date && row.inspection_start_date.trim()); // Skip blank inspection dates

        console.log('Total properties:', parsed.data.length);
        console.log('After filtering (with inspection dates):', parsedData.length);
        console.log('Parsed data sample:', parsedData.slice(0, 3));
        
        setProperties(parsedData);
        setFilteredProperties(parsedData);
        
        // Extract unique values with cleaning
        const uniqueStates = [...new Set(parsedData.map(p => p.state).filter(Boolean))].sort();
        
        // Use district instead of city (cleaner data)
        const uniqueDistricts = [...new Set(parsedData.map(p => p.district).filter(Boolean))].sort();
        
        const uniqueBanks = [...new Set(parsedData.map(p => p.bank_name).filter(Boolean))].sort();
        
        setStates(uniqueStates);
        setCities(uniqueDistricts); // Using districts but keeping state variable name
        setBanks(uniqueBanks);
        setLoading(false);
        
        // Load contact data
        loadContactData();
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    const loadContactData = async () => {
      try {
        const response = await fetch('/data/contact_output.csv');
        const text = await response.text();
        
        // Use the same Papa parser function
        const Papa = {
          parse: (input, config = {}) => {
            const rows = [];
            let currentRow = [];
            let currentCell = '';
            let insideQuotes = false;
            
            for (let i = 0; i < input.length; i++) {
              const char = input[i];
              const nextChar = input[i + 1];
              
              if (char === '"') {
                if (insideQuotes && nextChar === '"') {
                  currentCell += '"';
                  i++;
                } else {
                  insideQuotes = !insideQuotes;
                }
              } else if (char === ',' && !insideQuotes) {
                currentRow.push(currentCell);
                currentCell = '';
              } else if ((char === '\n' || char === '\r') && !insideQuotes) {
                if (char === '\r' && nextChar === '\n') {
                  i++;
                }
                if (currentCell || currentRow.length > 0) {
                  currentRow.push(currentCell);
                  if (currentRow.some(cell => cell.trim())) {
                    rows.push(currentRow);
                  }
                  currentRow = [];
                  currentCell = '';
                }
              } else {
                currentCell += char;
              }
            }
            
            if (currentCell || currentRow.length > 0) {
              currentRow.push(currentCell);
              if (currentRow.some(cell => cell.trim())) {
                rows.push(currentRow);
              }
            }
            
            if (rows.length === 0) return { data: [] };
            
            const headers = rows[0];
            const data = rows.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header.trim()] = (row[index] || '').trim();
              });
              return obj;
            });
            
            return { data };
          }
        };
        
        const parsed = Papa.parse(text);
        const contactMap = {};
        
        console.log('Parsed contact CSV:', parsed.data.length, 'rows');
        console.log('Sample contact row:', parsed.data[0]);
        
        parsed.data.forEach(row => {
          if (row.property_id) {
            contactMap[row.property_id] = {
              name: row.name,
              email: row.email,
              mobile: row.mobile,
              branch: row.branch,
              detail_url: row.detail_url
            };
          }
        });
        
        setContactData(contactMap);
        console.log('Contact data loaded:', Object.keys(contactMap).length, 'entries');
        console.log('Sample contact entry:', contactMap[Object.keys(contactMap)[0]]);
      } catch (error) {
        console.error('Error loading contact data:', error);
      }
    };

    loadData();
  }, []);

  // Parse price string to number (handles "42 Lac", "19.12 Lac", etc.)
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.match(/([\d.]+)\s*Lac/i);
    if (match) {
      return parseFloat(match[1]);
    }
    return 0;
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...properties];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.property_address?.toLowerCase().includes(query) ||
        p.borrower_name?.toLowerCase().includes(query) ||
        p.property_id?.toLowerCase().includes(query) ||
        p.district?.toLowerCase().includes(query)
      );
    }

    // State filter
    if (selectedState) {
      filtered = filtered.filter(p => p.state === selectedState);
    }

    // District filter (using selectedCity variable but filtering by district)
    if (selectedCity) {
      filtered = filtered.filter(p => p.district === selectedCity);
    }

    // Bank filter
    if (selectedBank) {
      filtered = filtered.filter(p => p.bank_name === selectedBank);
    }

    // Price filters
    if (minPrice) {
      const min = parseFloat(minPrice);
      filtered = filtered.filter(p => parsePrice(p.reserve_price) >= min);
    }

    if (maxPrice) {
      const max = parseFloat(maxPrice);
      filtered = filtered.filter(p => parsePrice(p.reserve_price) <= max);
    }

    // Sorting
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => parsePrice(a.reserve_price) - parsePrice(b.reserve_price));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => parsePrice(b.reserve_price) - parsePrice(a.reserve_price));
    } else if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.auction_start_date) - new Date(a.auction_start_date));
    }

    setFilteredProperties(filtered);
  }, [searchQuery, selectedState, selectedCity, selectedBank, minPrice, maxPrice, sortBy, properties]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedState('');
    setSelectedCity('');
    setSelectedBank('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('date');
  };

  const activeFiltersCount = [selectedState, selectedCity, selectedBank, minPrice, maxPrice].filter(Boolean).length;

  // Handle modal
  const openModal = (property) => {
    setSelectedProperty(property);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProperty(null);
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#10b981',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '18px'
      }}>
        Loading auction properties...
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      fontFamily: '"JetBrains Mono", monospace',
      color: '#e2e8f0',
      padding: '0'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(15, 23, 42, 0.95)',
        borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
        padding: '16px',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Top Row - Back Button & Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <a 
                href="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#10b981',
                  textDecoration: 'none',
                  fontSize: '13px',
                  transition: 'opacity 0.2s',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  background: 'rgba(16, 185, 129, 0.1)',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                <ArrowLeft size={14} />
                Back
              </a>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h1 style={{ 
                  margin: 0,
                  fontSize: 'clamp(18px, 4vw, 24px)',
                  fontWeight: '700',
                  color: '#10b981',
                  letterSpacing: '-0.5px'
                }}>
                  Auction Properties
                </h1>
                <div style={{ 
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  color: '#94a3b8',
                  marginTop: '4px'
                }}>
                  {filteredProperties.length} {filteredProperties.length === 1 ? 'Property' : 'Properties'}
                </div>
              </div>
            </div>

            {/* Search Bar - Full Width on Mobile */}
            <div style={{ position: 'relative', width: '100%' }}>
              <Search size={16} style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#64748b',
                pointerEvents: 'none'
              }} />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  background: 'rgba(30, 41, 59, 0.8)',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#10b981'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(100, 116, 139, 0.3)'}
              />
            </div>

            {/* Filter & Sort Row */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {/* Show Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  padding: '10px 14px',
                  background: showFilters ? 'rgba(16, 185, 129, 0.2)' : 'rgba(30, 41, 59, 0.8)',
                  border: `1px solid ${showFilters ? '#10b981' : 'rgba(100, 116, 139, 0.3)'}`,
                  borderRadius: '8px',
                  color: '#10b981',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: '"JetBrains Mono", monospace',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                <SlidersHorizontal size={14} />
                Filters
                {activeFiltersCount > 0 && (
                  <span style={{
                    background: '#10b981',
                    color: '#0f172a',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}>
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '10px 12px',
                  background: 'rgba(30, 41, 59, 0.8)',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: '"JetBrains Mono", monospace',
                  outline: 'none'
                }}
              >
                <option value="date">Recent First</option>
                <option value="price-low">Price: Low→High</option>
                <option value="price-high">Price: High→Low</option>
              </select>

              {/* Clear All Button */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontFamily: '"JetBrains Mono", monospace',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <X size={14} />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '12px',
              border: '1px solid rgba(100, 116, 139, 0.2)'
            }}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px'
              }}>
                {/* State Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none'
                    }}
                  >
                    <option value="">All States</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                {/* District Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    District
                  </label>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none'
                    }}
                  >
                    <option value="">All Districts</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                {/* Bank Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Bank
                  </label>
                  <select
                    value={selectedBank}
                    onChange={(e) => setSelectedBank(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none'
                    }}
                  >
                    <option value="">All Banks</option>
                    {banks.map(bank => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>

                {/* Min Price */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Min (Lac)
                  </label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="0"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '13px',
                      fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Max Price */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Max (Lac)
                  </label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="∞"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '13px',
                      fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Properties Grid */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '16px' }}>
        {filteredProperties.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#64748b'
          }}>
            <Home size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>No properties found</h3>
            <p style={{ fontSize: '14px' }}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
            gap: '16px'
          }}>
            {filteredProperties.map((property, index) => (
              <PropertyCard key={property.property_id || index} property={property} openModal={openModal} />
            ))}
          </div>
        )}
      </div>

      {/* Modal for Contact Information */}
      {showModal && selectedProperty && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}
        onClick={closeModal}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '16px',
            padding: 'clamp(16px, 4vw, 32px)',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '85vh',
            overflowY: 'auto',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#ef4444',
                fontSize: '20px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
            >
              <X size={18} />
            </button>

            {/* Modal Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#10b981',
                marginBottom: '8px',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                Contact Information
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#94a3b8',
                marginBottom: '12px'
              }}>
                Get in touch with the bank representative for this property
              </p>
              <div style={{
                fontSize: '12px',
                color: '#64748b',
                padding: '8px 12px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                Property ID: <span style={{ color: '#10b981', fontWeight: '600' }}>{selectedProperty.property_id}</span>
              </div>
            </div>

            {/* Property Details Section */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#10b981',
                marginBottom: '16px',
                letterSpacing: '0.5px'
              }}>
                PROPERTY INFORMATION
              </div>
              
              {/* Property Address */}
              {selectedProperty.property_address && (
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <MapPin size={12} />
                    Property Address
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#e2e8f0',
                    lineHeight: '1.5'
                  }}>
                    {selectedProperty.property_address}
                  </div>
                </div>
              )}

              {/* Pincode */}
              {selectedProperty.pincode && (
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Pincode
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: '#e2e8f0',
                    fontWeight: '600',
                    fontFamily: '"JetBrains Mono", monospace'
                  }}>
                    {selectedProperty.pincode}
                  </div>
                </div>
              )}

              {/* Title Deed */}
              {selectedProperty.title_deed && (
                <div style={{
                  marginBottom: '0',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Home size={12} />
                    Title Deed
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#e2e8f0',
                    lineHeight: '1.5'
                  }}>
                    {selectedProperty.title_deed}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Details */}
            {contactData[selectedProperty.property_id] ? (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#10b981',
                  marginBottom: '16px',
                  letterSpacing: '0.5px'
                }}>
                  CONTACT INFORMATION
                </div>
                {/* Name */}
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Contact Person
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: '#e2e8f0',
                    fontWeight: '600'
                  }}>
                    {contactData[selectedProperty.property_id].name}
                  </div>
                </div>

                {/* Email */}
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </div>
                  <a 
                    href={`mailto:${contactData[selectedProperty.property_id].email}`}
                    style={{
                      fontSize: '14px',
                      color: '#10b981',
                      textDecoration: 'none',
                      wordBreak: 'break-all'
                    }}
                  >
                    {contactData[selectedProperty.property_id].email}
                  </a>
                </div>

                {/* Mobile */}
                <div style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Mobile Number
                  </div>
                  <a 
                    href={`tel:${contactData[selectedProperty.property_id].mobile}`}
                    style={{
                      fontSize: '18px',
                      color: '#10b981',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}
                  >
                    {contactData[selectedProperty.property_id].mobile}
                  </a>
                </div>

                {/* Branch */}
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '8px',
                  border: '1px solid rgba(100, 116, 139, 0.2)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Branch Name
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#e2e8f0',
                    lineHeight: '1.4'
                  }}>
                    {contactData[selectedProperty.property_id].branch}
                  </div>
                </div>
              </div>
            ) : (
              // No contact info available
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#64748b'
              }}>
                <p>Contact information not available for this property.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PropertyCard = ({ property, openModal }) => {
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.6)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid rgba(100, 116, 139, 0.2)',
      transition: 'all 0.3s',
      cursor: 'pointer'
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.1)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.2)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Property ID Badge */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <span style={{
          fontSize: '11px',
          padding: '4px 10px',
          background: 'rgba(16, 185, 129, 0.1)',
          color: '#10b981',
          borderRadius: '6px',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          fontWeight: '600',
          letterSpacing: '0.5px'
        }}>
          {property.property_id}
        </span>
      </div>

      {/* Reserve Price */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ 
          fontSize: '28px',
          fontWeight: '700',
          color: '#10b981',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <IndianRupee size={24} />
          {property.reserve_price || 'N/A'}
        </div>
        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
          Reserve Price
        </div>
      </div>

      {/* Bank */}
      <div style={{ 
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px'
      }}>
        <Building2 size={16} style={{ color: '#64748b', marginTop: '2px', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Bank</div>
          <div style={{ fontSize: '14px', color: '#e2e8f0', marginTop: '2px' }}>
            {property.bank_name || 'N/A'}
          </div>
        </div>
      </div>

      {/* Address */}
      <div style={{ 
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px'
      }}>
        <MapPin size={16} style={{ color: '#64748b', marginTop: '2px', flexShrink: 0 }} />
        <div>
          <div style={{ 
            fontSize: '13px', 
            color: '#cbd5e1',
            lineHeight: '1.4',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {property.property_address || 'Address not available'}
          </div>
        </div>
      </div>

      {/* District & State */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '12px'
      }}>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>District</div>
          <div style={{ fontSize: '14px', color: '#e2e8f0', marginTop: '2px' }}>
            {property.district || 'N/A'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>State</div>
          <div style={{ fontSize: '14px', color: '#e2e8f0', marginTop: '2px' }}>
            {property.state || 'N/A'}
          </div>
        </div>
      </div>

      {/* Area & Ownership */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Area</div>
          <div style={{ fontSize: '14px', color: '#e2e8f0', marginTop: '2px' }}>
            {property.built_up_area || property.carpet_area || 'N/A'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Ownership</div>
          <div style={{ fontSize: '14px', color: '#e2e8f0', marginTop: '2px' }}>
            {property.ownership_type || 'N/A'}
          </div>
        </div>
      </div>

      {/* Auction Period */}
      {property.auction_start_date && property.auction_end_date && (
        <div style={{
          padding: '10px',
          background: 'rgba(16, 185, 129, 0.1)',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid rgba(16, 185, 129, 0.2)'
        }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '4px'
          }}>
            <Calendar size={14} />
            AUCTION PERIOD
          </div>
          <div style={{ fontSize: '12px', color: '#cbd5e1' }}>
            {property.auction_start_date} - {property.possession_status || 'Physical'}
          </div>
        </div>
      )}

      {/* View Details Button */}
      <button
        onClick={() => openModal(property)}
        style={{
          display: 'block',
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: '#fff',
          textAlign: 'center',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          transition: 'all 0.2s',
          border: 'none',
          cursor: 'pointer',
          fontFamily: '"JetBrains Mono", monospace'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        VIEW FULL DETAILS →
      </button>
    </div>
  );
};

export default AuctionDashboard;
